import { useEffect, useState } from 'react';

import { zMapPack, type MapPack } from '../model/map-pack.js';

/**
 * Resolving a scenario's map pack (M5-D11).
 *
 * > Geometry … is a **client-side asset keyed by scenario name**, resolved by the
 * > same convention `theme/imagery.tsx` already uses for art
 * > (`<base>mappacks/<scenario-slug>.json`).
 *
 * Same slug rule as the art slots, same `BASE_URL`, same one-request-per-miss
 * discipline — and the result is remembered, because every tab switch remounts
 * the pane and a scenario's geometry does not change inside a game.
 *
 * The pack is **parsed, not cast**. It is a file on a static host, which is one
 * of the few things this client reads that no schema on the wire covers, so it
 * gets its own: a pack with a typo'd key is a loud failure at load rather than a
 * zone quietly missing from the theatre.
 */

const packs = new Map<string, Promise<MapPackState>>();

export type MapPackState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly pack: MapPack }
  /** No file for this scenario. The board says so; it does not draw an empty world. */
  | { readonly status: 'absent' }
  /** A file that is not a pack. Never treated as absent — somebody has to fix it. */
  | { readonly status: 'invalid'; readonly reason: string };

export function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export function packUrl(scenario: string): string {
  return `${import.meta.env.BASE_URL}mappacks/${slugify(scenario)}.json`;
}

async function fetchPack(scenario: string): Promise<MapPackState> {
  let body: unknown;
  try {
    const response = await fetch(packUrl(scenario));
    if (!response.ok) return { status: 'absent' };
    body = await response.json();
  } catch {
    // A network error and a 404 are the same thing to a console that is deciding
    // whether it has geometry: it does not.
    return { status: 'absent' };
  }
  const parsed = zMapPack.safeParse(body);
  if (!parsed.success) {
    return { status: 'invalid', reason: parsed.error.issues[0]?.message ?? 'MALFORMED' };
  }
  // A pack that names a different scenario is a mis-slugged filename, which
  // would otherwise draw one exercise's geography under another's regions.
  if (parsed.data.scenario !== scenario) {
    return { status: 'invalid', reason: 'PACK IS FOR ' + parsed.data.scenario };
  }
  return { status: 'ready', pack: parsed.data };
}

export function loadMapPack(scenario: string): Promise<MapPackState> {
  const key = slugify(scenario);
  const hit = packs.get(key);
  if (hit !== undefined) return hit;
  const pending = fetchPack(scenario);
  packs.set(key, pending);
  return pending;
}

/** The pack for this scenario, once it has resolved. `loading` until then. */
export function useMapPack(scenario: string): MapPackState {
  const [state, setState] = useState<MapPackState>({ status: 'loading' });
  useEffect(() => {
    let live = true;
    setState({ status: 'loading' });
    void loadMapPack(scenario).then((resolved) => {
      if (live) setState(resolved);
    });
    return () => {
      live = false;
    };
  }, [scenario]);
  return state;
}
